// The devices a map widget shows, as GeoJSON for MapLibre's clustering source (WEB-F-DEV-05).
import type { DeviceState } from "../generated/widget-fixture";
import type { WidgetInput } from "../types";

export interface Placed {
  id: string;
  name: string;
  state: DeviceState["state"];
  lat: number;
  lon: number;
}

/** The widget's devices: its source's members, or every device in the input (a device page shows its own). */
export function mapDevices(input: WidgetInput): { all: string[]; placed: Placed[] } {
  const all = input.members ?? Object.keys(input.devices ?? {});
  const placed = all.flatMap((id) => {
    const d = input.devices?.[id];
    const p = d?.position;
    return d && p && Number.isFinite(p.lat) && Number.isFinite(p.lon)
      ? [{ id, name: d.name, state: d.state, lat: p.lat, lon: p.lon }]
      : [];
  });
  return { all, placed };
}

export function toFeatures(placed: Placed[]) {
  return {
    type: "FeatureCollection" as const,
    features: placed.map((d) => ({
      type: "Feature" as const,
      id: d.id,
      properties: { id: d.id, name: d.name, state: d.state },
      geometry: { type: "Point" as const, coordinates: [d.lon, d.lat] },
    })),
  };
}

/** The devices inside [[west, south], [east, north]]. */
export function inside(placed: Placed[], [[west, s], [east, n]]: [[number, number], [number, number]]): string[] {
  // MapLibre's bounds are unwrapped (east may pass 180); a view 360° wide or more holds every longitude.
  if (east - west >= 360) return placed.filter((d) => d.lat >= s && d.lat <= n).map((d) => d.id);
  const wrap = (lon: number) => ((((lon + 180) % 360) + 360) % 360) - 180;
  const w = wrap(west);
  const e = wrap(east);
  // A view across the antimeridian has its west edge east of its east edge once wrapped.
  const inLon = (lon: number) => (w <= e ? lon >= w && lon <= e : lon >= w || lon <= e);
  return placed.filter((d) => d.lat >= s && d.lat <= n && inLon(d.lon)).map((d) => d.id);
}

/** [[west, south], [east, north]] around the devices, or undefined without any. */
export function bounds(placed: Placed[]): [[number, number], [number, number]] | undefined {
  if (placed.length === 0) return undefined;
  const lons = placed.map((d) => d.lon);
  const lats = placed.map((d) => d.lat);
  return [
    [Math.min(...lons), Math.min(...lats)],
    [Math.max(...lons), Math.max(...lats)],
  ];
}

/** A device's track on the Map screen (maps-2 MAP2-20): its positions, oldest first, and the time marks' labels. */
export interface TrackLine {
  points: { lat: number; lon: number }[];
  marks: { lat: number; lon: number; label: string }[];
}

/** What only the Map screen adds to the map widget (maps-2). */
export interface MapScreen {
  /** The style to draw instead of the region's street map (Satellite); undefined keeps the street map. */
  style?: string;
  labels?: boolean;
  track?: TrackLine;
  placing?: boolean;
  onPlace?: (lon: number, lat: number) => void;
  /** Where to start without a device on the map (MAP2-11); the widget then draws the map even with none. */
  start?:
    | { kind: "bounds"; bounds: [[number, number], [number, number]] }
    | { kind: "center"; center: [number, number]; zoom: number };
  /** My position (MAP2-12): MapLibre's locate button. */
  locate?: boolean;
}
